import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { findItemByOptionTokenAndCustomizations } from "@faire/retailer-visitor-shared/lib/findItemByOptionTokenAndCustomizations";
import { getCartProductOptionQuantitiesMap, getCartProductQuantitiesMap } from "@faire/retailer-visitor-shared/lib/getCartItemQuantitiesMap";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { buildUpdateCartProto } from "@faire/retailer-visitor-shared/lib/updateCartV2";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { ICartItemForUpdate } from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useQuery as useLeanBrandsMapQuery, useInvalidateQuery as useInvalidateLeanBrandsMapQuery } from "@faire/web-api--source/api/v2/carts/cartToken/brands/get-hooks";
import { QueryParameters as CartItemInventoryParams } from "@faire/web-api--source/api/v2/carts/cartToken/item-inventory/get";
import { useQuery as useCartItemInventoryQuery, useInvalidateQuery as useInvalidateCartItemInventoryQuery } from "@faire/web-api--source/api/v2/carts/cartToken/item-inventory/get-hooks";
import { ICartItemV2 } from "@faire/web-api--source/faire/carts/ICartItemV2";
import { useCallback, useMemo } from "react";
export var useCartItems = function(arg) {
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _arg_inventoryParams;
    var _useCartItemInventoryQuery = useCartItemInventoryQuery(getCartToken(), (_arg_inventoryParams = arg === null || arg === void 0 ? void 0 : arg.inventoryParams) !== null && _arg_inventoryParams !== void 0 ? _arg_inventoryParams : CartItemInventoryParams.build({}), {
        enabled: isLoggedInRetailer,
        refetchOnMount: false
    }), cartInventoryData = _useCartItemInventoryQuery.data, isLoading = _useCartItemInventoryQuery.isPending;
    var _useLeanBrandsMapQuery = useLeanBrandsMapQuery(getCartToken(), arg === null || arg === void 0 ? void 0 : arg.leanBrandsMapParams, {
        enabled: isLoggedInRetailer,
        refetchOnMount: false
    }), leanBrandsData = _useLeanBrandsMapQuery.data;
    var leanCartItemMap = cartInventoryData === null || cartInventoryData === void 0 ? void 0 : cartInventoryData.cart_item_token_to_inventory_item;
    var totalBrandsInCartFormatted = cartInventoryData === null || cartInventoryData === void 0 ? void 0 : cartInventoryData.total_brands_in_cart_formatted;
    var leanBrandsMap = leanBrandsData === null || leanBrandsData === void 0 ? void 0 : leanBrandsData.brand_info;
    var leanBrands = useMemo(function() {
        return Object.values(leanBrandsMap !== null && leanBrandsMap !== void 0 ? leanBrandsMap : {}).filter(isNotUndefined);
    }, [
        leanBrandsMap
    ]);
    var cartItems = useMemo(function() {
        return Object.values(leanCartItemMap !== null && leanCartItemMap !== void 0 ? leanCartItemMap : {}).filter(isNotUndefined);
    }, [
        leanCartItemMap
    ]);
    var itemQuantityInCartByToken = useMemo(function() {
        return getCartProductOptionQuantitiesMap(cartItems);
    }, [
        cartItems
    ]);
    var productTileQuantityInCartByToken = useMemo(function() {
        return getCartProductQuantitiesMap(cartItems);
    }, [
        cartItems
    ]);
    var getItemByToken = useCallback(function(cartItemToken) {
        return leanCartItemMap === null || leanCartItemMap === void 0 ? void 0 : leanCartItemMap[cartItemToken];
    }, [
        leanCartItemMap
    ]);
    var getItemQuantityInCart = useCallback(function(productOptionToken) {
        var _itemQuantityInCartByToken_productOptionToken;
        return (_itemQuantityInCartByToken_productOptionToken = itemQuantityInCartByToken[productOptionToken]) !== null && _itemQuantityInCartByToken_productOptionToken !== void 0 ? _itemQuantityInCartByToken_productOptionToken : 0;
    }, [
        itemQuantityInCartByToken
    ]);
    var getProductTileQuantityInCart = useCallback(function(productToken) {
        var _productTileQuantityInCartByToken_productToken;
        return (_productTileQuantityInCartByToken_productToken = productTileQuantityInCartByToken[productToken]) !== null && _productTileQuantityInCartByToken_productToken !== void 0 ? _productTileQuantityInCartByToken_productToken : 0;
    }, [
        productTileQuantityInCartByToken
    ]);
    var getItemByProductOptionTokenAndCustomizations = useCallback(function(token, customizations) {
        return findItemByOptionTokenAndCustomizations(token, customizations, cartItems);
    }, [
        cartItems
    ]);
    var isBrandInCart = function(brandToken) {
        return isNotUndefined(leanBrandsMap === null || leanBrandsMap === void 0 ? void 0 : leanBrandsMap[brandToken]);
    };
    var createUpdateCartItemWithProperties = useCallback(function(item, properties) {
        if (item.type === ICartItemV2.Type.OPEN_SIZING) {
            var items = [];
            var _item_token;
            var cartOptionTokens = _to_consumable_array(item.associated_cart_item_tokens).concat([
                (_item_token = item.token) !== null && _item_token !== void 0 ? _item_token : ""
            ]);
            cartOptionTokens === null || cartOptionTokens === void 0 ? void 0 : cartOptionTokens.forEach(function(token) {
                var itemInStore = getItemByToken(token !== null && token !== void 0 ? token : "");
                if (!itemInStore) {
                    return;
                }
                var _itemInStore_customizations;
                items.push(ICartItemForUpdate.build(_object_spread({
                    token: token,
                    brand_token: item.brand_token,
                    product_token: itemInStore.product_token,
                    product_option_token: itemInStore.product_option_token,
                    quantity: itemInStore.quantity,
                    customizations: (_itemInStore_customizations = itemInStore.customizations) !== null && _itemInStore_customizations !== void 0 ? _itemInStore_customizations : []
                }, properties)));
            });
            return items;
        }
        return [
            buildUpdateCartProto(item, properties, false)
        ];
    }, [
        getItemByToken
    ]);
    return {
        leanCartItemMap: leanCartItemMap,
        totalBrandsInCartFormatted: totalBrandsInCartFormatted,
        leanBrandsMap: leanBrandsMap,
        leanBrands: leanBrands,
        cartItems: cartItems,
        getItemByToken: getItemByToken,
        getItemQuantityInCart: getItemQuantityInCart,
        getProductTileQuantityInCart: getProductTileQuantityInCart,
        getItemByProductOptionTokenAndCustomizations: getItemByProductOptionTokenAndCustomizations,
        isBrandInCart: isBrandInCart,
        createUpdateCartItemWithProperties: createUpdateCartItemWithProperties,
        isLoading: isLoading
    };
};
export var useInvalidateCartItems = function(arg) {
    var invalidateCartItemInventoryQuery = useInvalidateCartItemInventoryQuery();
    var invalidateLeanBrandsMapQuery = useInvalidateLeanBrandsMapQuery();
    var invalidateCartItemInventory = useCallback(function() {
        var _arg_inventoryParams;
        return invalidateCartItemInventoryQuery(getCartToken(), (_arg_inventoryParams = arg === null || arg === void 0 ? void 0 : arg.inventoryParams) !== null && _arg_inventoryParams !== void 0 ? _arg_inventoryParams : CartItemInventoryParams.build({}), undefined, {
            refetchType: "all"
        });
    }, [
        arg === null || arg === void 0 ? void 0 : arg.inventoryParams,
        invalidateCartItemInventoryQuery
    ]);
    var invalidateLeanBrandsMap = useCallback(function() {
        return invalidateLeanBrandsMapQuery(getCartToken(), arg === null || arg === void 0 ? void 0 : arg.leanBrandsMapParams, undefined, {
            refetchType: "all"
        });
    }, [
        arg === null || arg === void 0 ? void 0 : arg.leanBrandsMapParams,
        invalidateLeanBrandsMapQuery
    ]);
    return {
        invalidateCartItemInventory: invalidateCartItemInventory,
        invalidateLeanBrandsMap: invalidateLeanBrandsMap
    };
};
